import React, { useMemo } from 'react';
import { Modal, Card, Collapse } from 'antd';
import { FileSearchOutlined, CloseOutlined } from '@ant-design/icons';
import {
  isEmpty,
  extractMktu,
  renderWipoFieldItem as renderItem,
  safeJsonStringify,
} from '../../../utils/helpers';

const { Panel } = Collapse;

const WipoModal = ({ open, onClose, data, imgSrc }) => {
  const mktu = useMemo(() => (data ? extractMktu(data) : { numbers: [], fullText: '' }), [data]);
  const rawJsonText = useMemo(() => (data ? safeJsonStringify(data) : ''), [data]);

  if (!data) return null;

  const title = (
    data?.Trademark
    || data?.['MARKGR/CURRENT/IMAGE@TEXT']
    || data?.['@INTREGN']
    || 'Без названия'
  );

  const regNo = data?.['Reg. No'] || data?.['Регистрационный номер'] || data?.['@INTREGN'];
  const wipoLink = regNo
    ? `https://www3.wipo.int/madrid/monitor/en/showData.jsp?ID=ROM.${encodeURIComponent(String(regNo).trim())}&DES=1`
    : null;

  // AI-данные (elements, colors, text_content, semantic_description)
  const hasAiSection = (Array.isArray(data.elements) && data.elements.length > 0) ||
    (Array.isArray(data.colors) && data.colors.length > 0) ||
    (Array.isArray(data.text_content) && data.text_content.length > 0) ||
    (typeof data.semantic_description === 'string' && data.semantic_description.trim() !== '');

  const aiBlock = hasAiSection ? (
    <div className="theme-detail-section">
      <h4 className="theme-detail-section__title">🤖 AI</h4>
      {Array.isArray(data.elements) && data.elements.length > 0 && renderItem('Элементы (ИИ)', data.elements)}
      {Array.isArray(data.colors) && data.colors.length > 0 && renderItem('Цвета (ИИ)', data.colors)}
      {Array.isArray(data.text_content) && data.text_content.length > 0 && renderItem('Текстовое содержание (ИИ)', data.text_content)}
      {typeof data.semantic_description === 'string' && data.semantic_description.trim() !== '' && (
        <div style={{ marginBottom: 8, padding: '4px 0' }}>
          <b className="theme-detail-field-label">Семантическое описание (ИИ):</b> {data.semantic_description.trim()}
        </div>
      )}
    </div>
  ) : null;

  const mainBlock = (
    <div className="theme-detail-section">
      <h4 className="theme-detail-section__title">📋 Основная информация</h4>
      {renderItem('Регистрационный номер', data?.['Reg. No'] || data?.['@INTREGN'])}
      {renderItem('Статус', data?.Status || data?.['MARKGR@STATUS'])}
      {renderItem('Страна происхождения', data?.Origin || data?.['MARKGR@OOCD'])}
      {renderItem('Правообладатель', data?.Holder || data?.['MARKGR/CURRENT/HOLGR/NAME'])}
      {renderItem('Адрес правообладателя', data?.['Holder Address'] || data?.['MARKGR/CURRENT/HOLGR/ADDRESS'])}
      {renderItem('Страна правообладателя', data?.['Holder country'] || data?.['MARKGR/CURRENT/HOLGR/ADDRESS/COUNTRY'])}
      {renderItem('Представитель', data?.Representative || data?.['MARKGR/CURRENT/REPGR/NAME'])}
      {renderItem('Адрес представителя', data?.['Rep. Address'] || data?.['MARKGR/CURRENT/REPGR/ADDRESS'])}
      {renderItem('Страна представителя', data?.['Rep. Country'] || data?.['MARKGR/CURRENT/REPGR/ADDRESS/COUNTRY'])}
      {renderItem('Номер базовой заявки', data?.['Basic app. no.'])}
      {renderItem('Номер базовой регистрации', data?.['Basic. reg. no.'])}
    </div>
  );

  const datesBlock = (
    <div className="theme-detail-section">
      <h4 className="theme-detail-section__title">📅 Даты</h4>
      {renderItem('Дата регистрации (межд.)', data?.['Reg. Date'] || data?.['MARKGR@INTREGD'])}
      {renderItem('Срок действия до', data?.['Exp. Date'] || data?.['MARKGR@EXPDATE'])}
      {renderItem('Дата публикации', data?.['MARKGR/ENN@PUBDATE'])}
      {renderItem('Дата извещения', data?.['MARKGR/ENN@NOTDATE'])}
    </div>
  );

  const linksBlock = (
    <div className="theme-detail-section">
      <h4 className="theme-detail-section__title">🔗 Ссылки</h4>
      {wipoLink ? (
        <div style={{ marginBottom: 8 }}>
          <b className="theme-detail-field-label">WIPO Madrid Monitor:</b>{' '}
          <a href={wipoLink} target="_blank" rel="noopener noreferrer">Ссылка на сайт WIPO</a>
        </div>
      ) : (
        <div style={{ color: '#6c757d' }}>Нет данных о ссылке</div>
      )}
    </div>
  );

  const mktuBlock = !isEmpty(mktu.numbers) || !isEmpty(mktu.fullText) ? (
    <div className="theme-detail-section">
      <h4 className="theme-detail-section__title">🏷️ Классы МКТУ</h4>
      {!isEmpty(mktu.numbers) && (
        <div style={{ marginBottom: 8 }}>
          <b className="theme-detail-field-label">Классы:</b> {mktu.numbers.join(', ')}
        </div>
      )}
      {renderItem('Венская классификация', data?.['Vienna Cl.'])}
      {!isEmpty(mktu.fullText) && (
        <Collapse>
          <Panel header="Показать полную расшифровку" key="mktu">
            <div style={{ whiteSpace: 'pre-wrap' }}>{mktu.fullText}</div>
          </Panel>
        </Collapse>
      )}
    </div>
  ) : null;

  const currentBlock = (
    <div className="theme-detail-section">
      <h4 className="theme-detail-section__title">📄 Детали WIPO (текущая запись)</h4>
      {renderItem('Текст знака (IMAGE@TEXT)', data?.['MARKGR/CURRENT/IMAGE@TEXT'])}
      {renderItem('Код офиса (OOCD)', data?.['MARKGR/CURRENT/HOLGR/ENTEST'] || data?.['MARKGR@OOCD'])}
      {renderItem('Страны назначения', data?.['MARKGR/CURRENT/DESPG2/DCPCD'])}
      {renderItem('Назначения (Designations)', data?.Designations)}
    </div>
  );

  const rawBlock = (
    <div className="theme-detail-section">
      <h4 className="theme-detail-section__title">🗂️ Полные данные WIPO (JSON)</h4>
      <div className="theme-preview-muted" style={{
        borderRadius: 6,
        padding: 12,
        maxHeight: '40vh',
        overflow: 'auto',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: 12,
        whiteSpace: 'pre',
      }}>
        {rawJsonText}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      destroyOnClose
      width="min(960px, 92vw)"
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', width: '100%' }}
      closeIcon={<CloseOutlined style={{ fontSize: 20, color: '#000' }} />}
    >
      <Card
        title={
          <div className="theme-modal-title">
            {title}
          </div>
        }
        cover={
          imgSrc ? (
            <div style={{ textAlign: 'center', padding: '20px 0', maxWidth: '100%', boxSizing: 'border-box' }}>
              <img
                src={imgSrc}
                alt={title}
                style={{
                  display: 'block',
                  margin: '0 auto',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  maxHeight: 'min(72vh, 85vw)',
                  borderRadius: 8,
                  objectFit: 'contain',
                }}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <FileSearchOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </div>
          )
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {mainBlock}
          {datesBlock}
          {linksBlock}
          {mktuBlock}
          {aiBlock}
          {currentBlock}
          {rawBlock}
        </div>
      </Card>
    </Modal>
  );
};

export default WipoModal;


